/**
 * Base skeleton component for loading states
 * @module components/common/Skeleton
 */

import React from 'react'
import { Skeleton as ShadcnSkeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export interface SkeletonProps {
  /**
   * Width of the skeleton (CSS value)
   */
  width?: string | number

  /**
   * Height of the skeleton (CSS value)
   */
  height?: string | number

  /**
   * Skeleton variant - text, circular, rectangular, rounded
   */
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded'

  /**
   * Animation type - pulse, wave, or false to disable
   */
  animation?: 'pulse' | 'wave' | false

  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * Converts pixel/percentage values to Tailwind className
 */
const convertToClassName = (width?: string | number, height?: string | number): string => {
  const classes: string[] = []

  if (width !== undefined) {
    if (typeof width === 'number') {
      classes.push(`w-[${width}px]`)
    } else {
      classes.push(`w-[${width}]`)
    }
  }

  if (height !== undefined) {
    if (typeof height === 'number') {
      classes.push(`h-[${height}px]`)
    } else {
      classes.push(`h-[${height}]`)
    }
  }

  return classes.join(' ')
}

/**
 * Base skeleton component
 *
 * Used to create loading placeholders that match expected content structure
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  width,
  height,
  variant = 'rectangular',
  animation = 'wave',
  className = ''
}) => {
  // Map MUI variants to shadcn/ui variants
  const shadcnVariant = variant === 'text' ? 'text' : variant === 'circular' ? 'circular' : 'default'

  // Convert width/height to className
  const sizeClassName = convertToClassName(width, height)

  // Combine all classNames
  const combinedClassName = cn(
    sizeClassName,
    variant === 'rounded' && 'rounded-lg',
    animation === false && '[animation:none]',
    className
  )

  return <ShadcnSkeleton variant={shadcnVariant} className={combinedClassName} />
}

/**
 * Skeleton text component for text placeholders
 */
export const SkeletonText: React.FC<Omit<SkeletonProps, 'variant'>> = (props) => {
  return <Skeleton {...props} variant="text" />
}

/**
 * Skeleton circular component for avatar placeholders
 */
export const SkeletonCircular: React.FC<Omit<SkeletonProps, 'variant'>> = (props) => {
  return <Skeleton {...props} variant="circular" />
}

/**
 * Skeleton rectangular component for box placeholders
 */
export const SkeletonRectangular: React.FC<Omit<SkeletonProps, 'variant'>> = (props) => {
  return <Skeleton {...props} variant="rectangular" />
}

/**
 * Skeleton rounded component for button/card placeholders
 */
export const SkeletonRounded: React.FC<Omit<SkeletonProps, 'variant'>> = (props) => {
  return <Skeleton {...props} variant="rounded" />
}
